/**
 * ADR 215/216 — pure helpers for an uploaded 2D floor plan (PNG/JPEG), the
 * counterpart of `mapGrid.ts` for continuous-tone imagery.
 *
 * A SLAM raster has three meaningful values (0 wall / 205 unknown / 254 free) and
 * `Minimap.themeRaster` maps them to two colors. A CAD sheet or a scan has no such
 * quantization: it is grey line work, aisle numbers, hatching. Quantizing it to
 * wall/free would erase exactly what makes it worth uploading, so it is recolored
 * by INK COVERAGE instead — dark pixels become the wall blue at strong alpha, the
 * paper becomes the same faint floor wash the SLAM path uses. Line weight, labels
 * and hatching survive; the panel keeps one visual language and stays legible on
 * the dark navy (`DIGITAL_TWIN_FRONTEND.md`).
 */

import { tokens } from "../styles/tokens";

export interface Rgb { r: number; g: number; b: number }

export function hexToRgb(hex: string): Rgb {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/** Below this ink coverage a pixel is paper, not line work. Chosen above JPEG's
 *  ringing around black lines on white, so a compressed scan does not come out
 *  as a uniform blue haze. */
const PAPER_INK = 0.14;
const INK_ALPHA_MIN = 55;
const INK_ALPHA_MAX = 235;
const PAPER_ALPHA = 46;   // same floor wash as the SLAM raster's "free"

/**
 * Recolor RGBA pixels in place. Source alpha is respected: a plan exported with a
 * transparent background stays transparent instead of gaining a paper wash.
 */
export function themePlanPixels(
  px: Uint8ClampedArray,
  ink: Rgb = hexToRgb(tokens.blue[300]),
  paper: Rgb = hexToRgb(tokens.blue[500]),
): void {
  for (let i = 0; i < px.length; i += 4) {
    const a = px[i + 3];
    if (a === 0) continue;
    // Rec. 601 luma — cheap, and hue is irrelevant once everything is recolored.
    const lum = (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) / 255;
    const coverage = 1 - lum;
    if (coverage < PAPER_INK) {
      px[i] = paper.r; px[i + 1] = paper.g; px[i + 2] = paper.b;
      px[i + 3] = Math.round((PAPER_ALPHA * a) / 255);
    } else {
      // Normalize coverage over the ink range so a light-grey pencil plan still
      // reaches full strength instead of rendering at the alpha floor.
      const t = (coverage - PAPER_INK) / (1 - PAPER_INK);
      px[i] = ink.r; px[i + 1] = ink.g; px[i + 2] = ink.b;
      px[i + 3] = Math.round(((INK_ALPHA_MIN + (INK_ALPHA_MAX - INK_ALPHA_MIN) * t) * a) / 255);
    }
  }
}

/**
 * Decode cap. The minimap paints ~300 CSS px wide, so a 6000 px architectural
 * export buys nothing and costs 24 MB of ImageData plus a per-pixel pass. Aspect
 * ratio is preserved — the georeference is a single resolution for both axes, so
 * a non-uniform fit would silently mis-place every marker.
 */
export function fitPlanSize(
  widthPx: number, heightPx: number, maxEdge: number,
): { width: number; height: number } {
  const longest = Math.max(widthPx, heightPx);
  if (longest <= maxEdge || longest === 0) {
    return { width: Math.max(1, widthPx), height: Math.max(1, heightPx) };
  }
  const k = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(widthPx * k)),
    height: Math.max(1, Math.round(heightPx * k)),
  };
}

export const PLAN_MAX_EDGE_PX = 1400;

/** Frame of a 2D map: pixel size plus the twin-frame placement of its
 *  TOP-left pixel — always the (min x, min y) corner. Shared by the uploaded
 *  plan and the SLAM raster so the minimap's world→pixel math has a single
 *  shape. */
export interface MapFrame2d {
  widthPx: number;
  heightPx: number;
  resolution: number;
  originX: number;
  originY: number;
}

/** World (twin-frame X,Y metres) → pixel in a `MapFrame2d`.
 *
 *  No Y flip — deliberately (ADR 311). The stored 2D frame is image-like: +y
 *  is South (ADR 25 "+Z = South", ADR 189 "stored y toward screen-south"), so
 *  y maps straight down the rows and the display reads like the 3D scene seen
 *  from above, north up. The previous bottom-left/y-up convention mirrored the
 *  whole minimap against the scene — invisible on symmetric rack fields,
 *  obvious on the first photoreal plan. */
export function worldToPixel(f: MapFrame2d, x: number, y: number): [number, number] {
  return [
    (x - f.originX) / f.resolution,
    (y - f.originY) / f.resolution,
  ];
}

/** Metres spanned by the frame, and its diagonal — the natural clamp for how far
 *  a view footprint should be allowed to run before it leaves the map (ADR 216). */
export function frameDiagonalM(f: MapFrame2d): number {
  const w = f.widthPx * f.resolution;
  const h = f.heightPx * f.resolution;
  return Math.hypot(w, h);
}

export interface Bbox { minX: number; minY: number; maxX: number; maxY: number }

/** Extent of a set of `[[x,y], …]` warehouse-frame polygons; null when empty. */
export function footprintBbox(polygons: ReadonlyArray<ReadonlyArray<ReadonlyArray<number>>>): Bbox | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const poly of polygons) {
    for (const p of poly) {
      if (p.length < 2 || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) continue;
      if (p[0] < minX) minX = p[0];
      if (p[0] > maxX) maxX = p[0];
      if (p[1] < minY) minY = p[1];
      if (p[1] > maxY) maxY = p[1];
    }
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;
  return { minX, minY, maxX, maxY };
}

/**
 * A first guess at where an uploaded plan sits: scale it so the warehouse's known
 * content (its mapped rack footprints) fits inside, centred, with a margin for the
 * sheet's own borders. Aspect comes from the image, so the guess never distorts —
 * it covers the content on the tight axis and overshoots on the other.
 *
 * The returned origin is the (min x, min y) corner — the frame's TOP-left under
 * the ADR 311 image-like display, which is also the north-west corner on site.
 *
 * A guess, not a calibration: it is the starting point an operator then nudges,
 * which beats making them derive m/px from a drawing scale by hand.
 */
export function fitFrameToContent(
  b: Bbox, widthPx: number, heightPx: number, margin = 0.06,
): { widthM: number; originX: number; originY: number } {
  const aspect = heightPx > 0 ? widthPx / heightPx : 1;
  const contentW = Math.max(b.maxX - b.minX, 0.001);
  const contentH = Math.max(b.maxY - b.minY, 0.001);
  const k = 1 + Math.max(margin, 0);
  const widthM = Math.max(contentW * k, contentH * k * aspect);
  const heightM = widthM / aspect;
  return {
    widthM,
    originX: b.minX - (widthM - contentW) / 2,
    originY: b.minY - (heightM - contentH) / 2,
  };
}
